import {
  CreateFolderDialog,
  DeleteFolderDialog,
  MoveBookmarkDialog,
  RenameFolderDialog,
} from "@/components/bookmarks/BookmarksDialogs";
import {
  BookmarksFolderSection,
  type FolderSectionHandlers,
} from "@/components/bookmarks/BookmarksFolderSection";
import {
  buildBookmarkFolderIndex,
  isFolderLocked,
  patchBookmarkFolderIndex,
  sameBookmarkContent,
  sameBookmarkOrder,
} from "@/components/bookmarks/bookmarksHelpers";
import Container from "@/components/ui/Container";
import { useErrorHandler } from "@/components/ui/ErrorHandlerProvider";
import {
  BodyText,
  CaptionText,
  CardTitle,
  PageTitle,
} from "@/components/ui/Typography";
import { useAuth } from "@/lib/AuthContext";
import { BRAND_COLORS } from "@/lib/brandColors";
import { logger } from "@/lib/logger";
import { useIsMobile } from "@/lib/useDeviceType";
import {
  createUserFolder,
  deleteUserFolder,
  fetchAllFolderBookmarks,
  fetchUserFolders,
  moveBookmarkToFolder,
  removeBookmarkById,
  renameUserFolder,
  reorderFolderBookmarks,
  UserBookmark,
  UserFolder,
} from "@/supabase/services/userFolders";
import { Ionicons } from "@expo/vector-icons";
import { useFocusEffect } from "@react-navigation/native";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  RefreshControl,
  StyleSheet,
} from "react-native";
import {
  DraxProvider,
  DraxScrollView,
  type SortableReorderEvent,
} from "react-native-drax";
import { Button, Spinner, XStack, YStack } from "tamagui";

type MoveTarget = {
  bookmark: UserBookmark;
  title: string;
  /** Folder the bookmark is currently in (explicit — avoids id type mismatches) */
  fromFolderId: number;
};

const REORDER_DEBOUNCE_MS = 250;

export function BookmarksScreen() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { showError, showSuccess } = useErrorHandler();

  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [folderBookmarks, setFolderBookmarks] = useState<
    Record<number, UserBookmark[]>
  >({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [renameFolder, setRenameFolder] = useState<UserFolder | null>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [deleteFolder, setDeleteFolder] = useState<UserFolder | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);

  /** Avoid double-commit from onReorder + container onReceiveDragDrop */
  const lastCrossMoveKeyRef = useRef<string | null>(null);
  /** Skip Drax onReorder after we already committed drop/move for these folders */
  const skipFolderReorderRef = useRef<Set<number>>(new Set());
  const folderBookmarksRef = useRef(folderBookmarks);
  const bookmarkFolderIndexRef = useRef<Map<number, number>>(new Map());
  folderBookmarksRef.current = folderBookmarks;

  const [listEpoch, setListEpoch] = useState<Record<number, number>>({});
  const reorderTimersRef = useRef<
    Record<number, ReturnType<typeof setTimeout>>
  >({});

  /**
   * Folder highlight: Drax clears container receivingStyle when pointer is
   * between items — keep it with local state + delayed exit.
   */
  const [hoverFolderId, setHoverFolderId] = useState<number | null>(null);
  /** Cross-folder: insert preview — same visual hole as within-folder reorder */
  const [crossDropTarget, setCrossDropTarget] = useState<{
    folderId: number;
    /** bookmark id to insert before; null = append at end */
    beforeItemId: number | null;
  } | null>(null);
  const hoverExitTimersRef = useRef<
    Record<number, ReturnType<typeof setTimeout>>
  >({});

  // Rebuild index only when bookmark data changes (not on hover/dialog typing)
  useEffect(() => {
    bookmarkFolderIndexRef.current = buildBookmarkFolderIndex(folderBookmarks);
  }, [folderBookmarks]);

  useEffect(() => {
    return () => {
      Object.values(hoverExitTimersRef.current).forEach(clearTimeout);
      Object.values(reorderTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const clearHoverExitTimer = useCallback((folderId: number) => {
    const t = hoverExitTimersRef.current[folderId];
    if (t) {
      clearTimeout(t);
      delete hoverExitTimersRef.current[folderId];
    }
  }, []);

  const markFolderHover = useCallback(
    (folderId: number) => {
      clearHoverExitTimer(folderId);
      setHoverFolderId((prev) => (prev === folderId ? prev : folderId));
    },
    [clearHoverExitTimer],
  );

  const markCrossDropTarget = useCallback(
    (folderId: number, beforeItemId: number | null) => {
      setCrossDropTarget((prev) => {
        if (
          prev?.folderId === folderId &&
          prev.beforeItemId === beforeItemId
        ) {
          return prev;
        }
        return { folderId, beforeItemId };
      });
    },
    [],
  );

  const scheduleFolderHoverExit = useCallback(
    (folderId: number) => {
      clearHoverExitTimer(folderId);
      hoverExitTimersRef.current[folderId] = setTimeout(() => {
        setHoverFolderId((prev) => (prev === folderId ? null : prev));
        setCrossDropTarget((prev) =>
          prev?.folderId === folderId ? null : prev,
        );
        delete hoverExitTimersRef.current[folderId];
      }, 160);
    },
    [clearHoverExitTimer],
  );

  const clearDragUi = useCallback(() => {
    Object.keys(hoverExitTimersRef.current).forEach((id) =>
      clearHoverExitTimer(Number(id)),
    );
    setHoverFolderId(null);
    setCrossDropTarget(null);
  }, [clearHoverExitTimer]);

  const closeRenameModal = useCallback(() => {
    setRenameFolder(null);
  }, []);

  const closeCreateModal = useCallback(() => {
    setCreateFolderOpen(false);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteFolder(null);
  }, []);

  const closeMoveModal = useCallback(() => {
    setMoveTarget(null);
  }, []);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoadError(null);
      const [userFolders, grouped] = await Promise.all([
        fetchUserFolders(user.id),
        fetchAllFolderBookmarks(user.id),
      ]);
      setFolders(userFolders);
      setFolderBookmarks(grouped);
      setExpanded((prev) => {
        const next: Record<number, boolean> = {};
        userFolders.forEach((f, i) => {
          const id = Number(f.id);
          next[id] = prev[id] ?? prev[f.id] ?? i === 0;
        });
        return next;
      });
    } catch (error: any) {
      logger.error("Error loading bookmarks page:", error);
      setLoadError(error);
    }
  }, [user?.id]);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      (async () => {
        setLoading(true);
        await load();
        if (active) setLoading(false);
      })();
      return () => {
        active = false;
      };
    }, [load]),
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  const totalCount = useMemo(
    () =>
      Object.values(folderBookmarks).reduce(
        (sum, list) => sum + (list?.length ?? 0),
        0,
      ),
    [folderBookmarks],
  );

  const moveTargets = useMemo(() => {
    if (!moveTarget) return [];
    const fromId = Number(moveTarget.fromFolderId);
    return folders
      .filter((f) => Number(f.id) !== fromId)
      .map((f) => ({ id: Number(f.id), title: f.title }));
  }, [folders, moveTarget]);

  const openMove = useCallback(
    (bookmark: UserBookmark, title: string, fromFolderId: number) => {
      setMoveTarget({ bookmark, title, fromFolderId: Number(fromFolderId) });
    },
    [],
  );

  const openRename = useCallback(
    (folder: UserFolder) => {
      if (isFolderLocked(folder)) {
        showError("This folder is locked and can’t be renamed.");
        return;
      }
      setRenameFolder(folder);
    },
    [showError],
  );

  const openDelete = useCallback(
    (folder: UserFolder) => {
      if (isFolderLocked(folder)) {
        showError("This folder is locked and can’t be deleted.");
        return;
      }
      setDeleteFolder(folder);
    },
    [showError],
  );

  const toggleExpanded = useCallback((folderId: number) => {
    setExpanded((prev) => {
      const isOpen = prev[folderId] !== false;
      return { ...prev, [folderId]: !isOpen };
    });
  }, []);

  const runRename = useCallback(
    async (name: string) => {
      if (!renameFolder || !user?.id) return;
      const next = name.trim();
      if (!next) {
        showError("Please enter a folder name");
        return;
      }
      setBusy(true);
      try {
        const updated = await renameUserFolder(renameFolder.id, next, user.id);
        setFolders((prev) =>
          prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)),
        );
        showSuccess("Folder renamed");
        closeRenameModal();
      } catch (error: any) {
        logger.error("Failed to rename folder:", error);
        showError(error?.message || "Failed to rename folder");
      } finally {
        setBusy(false);
      }
    },
    [renameFolder, user?.id, showError, showSuccess, closeRenameModal],
  );

  const runCreateFolder = useCallback(
    async (name: string) => {
      if (!user?.id) return;
      const next = name.trim();
      if (!next) {
        showError("Please enter a folder name");
        return;
      }
      setBusy(true);
      try {
        const created = await createUserFolder(next, user.id);
        const id = Number(created.id);
        setFolders((prev) => [created, ...prev]);
        setFolderBookmarks((prev) => ({ ...prev, [id]: prev[id] ?? [] }));
        setExpanded((prev) => ({ ...prev, [id]: true }));
        showSuccess("Folder created");
        closeCreateModal();
      } catch (error: any) {
        logger.error("Failed to create folder:", error);
        showError(error?.message || "Failed to create folder");
      } finally {
        setBusy(false);
      }
    },
    [user?.id, showError, showSuccess, closeCreateModal],
  );

  const runDeleteFolder = useCallback(async () => {
    if (!deleteFolder || !user?.id) return;
    setBusy(true);
    try {
      await deleteUserFolder(deleteFolder.id, user.id);
      showSuccess("Folder deleted");
      closeDeleteModal();
      await load();
    } catch (error: any) {
      logger.error("Failed to delete folder:", error);
      showError(error?.message || "Failed to delete folder");
    } finally {
      setBusy(false);
    }
  }, [
    deleteFolder,
    user?.id,
    showSuccess,
    showError,
    closeDeleteModal,
    load,
  ]);

  const runRemoveBookmark = useCallback(
    async (bookmark: UserBookmark) => {
      if (!user?.id) return;

      const folderId = Number(bookmark.user_folder_id);
      const bookmarkId = Number(bookmark.id);
      let snapshot: UserBookmark[] = [];

      patchBookmarkFolderIndex(bookmarkFolderIndexRef.current, bookmarkId, null);

      setFolderBookmarks((prev) => {
        const list =
          prev[folderId] ??
          (prev as Record<string, UserBookmark[]>)[String(folderId)] ??
          [];
        snapshot = list;
        return {
          ...prev,
          [folderId]: list.filter((b) => Number(b.id) !== bookmarkId),
        };
      });

      try {
        await removeBookmarkById(bookmarkId, user.id);
        showSuccess("Removed");
      } catch (error: any) {
        logger.error("Failed to remove bookmark:", error);
        patchBookmarkFolderIndex(
          bookmarkFolderIndexRef.current,
          bookmarkId,
          folderId,
        );
        setFolderBookmarks((prev) => ({
          ...prev,
          [folderId]: snapshot,
        }));
        showError(error?.message || "Failed to remove bookmark");
      }
    },
    [user?.id, showSuccess, showError],
  );

  const persistCrossFolderMove = useCallback(
    (
      bookmarkId: number,
      targetFolderId: number,
      orderedTargetIds?: number[],
    ) => {
      if (!user?.id) return;
      void (async () => {
        try {
          const result = await moveBookmarkToFolder(
            bookmarkId,
            targetFolderId,
            user.id,
            orderedTargetIds,
          );
          showSuccess(
            result === "already_exists" || result === "merged"
              ? "Already in this folder"
              : "Moved",
          );
          // Legacy 'merged' deleted the source — reload; already_exists is a no-op
          if (result === "merged") await load();
        } catch (error: any) {
          logger.error("Failed to move bookmark:", error);
          showError(error?.message || "Failed to move bookmark");
          await load();
        }
      })();
    },
    [user?.id, showSuccess, showError, load],
  );

  const persistFolderOrder = useCallback(
    (folderId: number, ordered: UserBookmark[]) => {
      if (!user?.id || ordered.length === 0) return;
      const ids = ordered.map((b) => Number(b.id));
      const existing = reorderTimersRef.current[folderId];
      if (existing) clearTimeout(existing);
      reorderTimersRef.current[folderId] = setTimeout(() => {
        delete reorderTimersRef.current[folderId];
        void (async () => {
          try {
            await reorderFolderBookmarks(folderId, ids);
          } catch (error: any) {
            logger.error("Failed to save bookmark order:", error);
            showError(error?.message || "Failed to save order");
            await load();
          }
        })();
      }, REORDER_DEBOUNCE_MS);
    },
    [user?.id, showError, load],
  );

  const persistFolderOrderRef = useRef(persistFolderOrder);
  persistFolderOrderRef.current = persistFolderOrder;

  /** Optimistic local reorder + persist */
  const applyWithinFolderReorder = useCallback(
    (folderId: number, next: UserBookmark[]) => {
      // Drop stale source-list updates after a cross-folder move
      const cleaned = next.filter((b) => {
        const owner = bookmarkFolderIndexRef.current.get(Number(b.id));
        return owner == null || owner === folderId;
      });
      if (cleaned.length === 0 && next.length > 0) {
        // All items belong elsewhere — ignore this reorder entirely
        return;
      }

      const current = folderBookmarksRef.current[folderId] ?? [];
      if (sameBookmarkOrder(current, cleaned)) return;

      folderBookmarksRef.current = {
        ...folderBookmarksRef.current,
        [folderId]: cleaned,
      };
      setFolderBookmarks((prev) => ({ ...prev, [folderId]: cleaned }));
      setListEpoch((prev) => ({
        ...prev,
        [folderId]: (prev[folderId] ?? 0) + 1,
      }));
      persistFolderOrderRef.current(folderId, cleaned);
    },
    [],
  );

  const applyWithinFolderReorderRef = useRef(applyWithinFolderReorder);
  applyWithinFolderReorderRef.current = applyWithinFolderReorder;

  /** Optimistic local move + persist (shared by DnD + Move dialog) */
  const applyCrossFolderMove = useCallback(
    (
      bookmarkId: number,
      targetFolderId: number,
      toIndex?: number,
      bookmarkHint?: UserBookmark,
    ) => {
      if (!user?.id) return;

      const targetId = Number(targetFolderId);
      const id = Number(bookmarkId);
      const bookmarks = folderBookmarksRef.current;

      let sourceFolderId = bookmarkFolderIndexRef.current.get(id);
      let moved: UserBookmark | undefined = bookmarkHint;

      if (sourceFolderId != null) {
        moved =
          bookmarks[sourceFolderId]?.find((b) => Number(b.id) === id) ??
          moved;
      } else {
        for (const [key, list] of Object.entries(bookmarks)) {
          const found = list.find((b) => Number(b.id) === id);
          if (found) {
            moved = found;
            sourceFolderId = Number(key);
            break;
          }
        }
      }

      if (!moved) return;
      if (sourceFolderId == null) {
        sourceFolderId = Number(moved.user_folder_id);
      }
      if (sourceFolderId == null || Number.isNaN(sourceFolderId)) return;
      if (sourceFolderId === targetId) return;

      const key =
        toIndex != null
          ? `${id}->${targetId}@${toIndex}`
          : `${id}->${targetId}@end`;

      if (
        lastCrossMoveKeyRef.current === key ||
        lastCrossMoveKeyRef.current?.startsWith(`${id}->${targetId}`)
      ) {
        return;
      }
      lastCrossMoveKeyRef.current = key;
      setTimeout(() => {
        lastCrossMoveKeyRef.current = null;
      }, 300);

      const destList = bookmarks[targetId] ?? [];
      const alreadyThere = destList.some(
        (b) => Number(b.id) !== id && sameBookmarkContent(moved!, b),
      );

      // Same lesson/course already in target — snap back to source, no delete/dup
      if (alreadyThere) {
        skipFolderReorderRef.current.add(sourceFolderId);
        skipFolderReorderRef.current.add(targetId);
        setTimeout(() => {
          skipFolderReorderRef.current.delete(sourceFolderId!);
          skipFolderReorderRef.current.delete(targetId);
        }, 450);
        clearDragUi();
        setListEpoch((prev) => ({
          ...prev,
          [sourceFolderId]: (prev[sourceFolderId] ?? 0) + 1,
          [targetId]: (prev[targetId] ?? 0) + 1,
        }));
        showSuccess("Already in this folder");
        return;
      }

      const dest = destList.filter((b) => Number(b.id) !== id);
      const at =
        toIndex != null
          ? Math.min(Math.max(toIndex, 0), dest.length)
          : dest.length;
      dest.splice(at, 0, { ...moved!, user_folder_id: targetId });
      const nextDest = dest.map((b, index) => ({
        ...b,
        user_folder_id: targetId,
        sort_order: index,
      }));
      const orderedTargetIds = nextDest.map((b) => Number(b.id));

      // Sync refs immediately so a following source onReorder can’t resurrect the item
      const nextBookmarks: Record<number, UserBookmark[]> = {
        ...bookmarks,
        [sourceFolderId]: (bookmarks[sourceFolderId] || []).filter(
          (b) => Number(b.id) !== id,
        ),
        [targetId]: nextDest,
      };
      patchBookmarkFolderIndex(
        bookmarkFolderIndexRef.current,
        id,
        targetId,
      );
      folderBookmarksRef.current = nextBookmarks;

      skipFolderReorderRef.current.add(sourceFolderId);
      skipFolderReorderRef.current.add(targetId);
      setTimeout(() => {
        skipFolderReorderRef.current.delete(sourceFolderId!);
        skipFolderReorderRef.current.delete(targetId);
      }, 450);

      setFolderBookmarks(nextBookmarks);
      setExpanded((prev) => ({ ...prev, [targetId]: true }));
      setListEpoch((prev) => ({
        ...prev,
        [sourceFolderId]: (prev[sourceFolderId] ?? 0) + 1,
        [targetId]: (prev[targetId] ?? 0) + 1,
      }));
      clearDragUi();
      persistCrossFolderMove(id, targetId, orderedTargetIds);
    },
    [user?.id, clearDragUi, persistCrossFolderMove, showSuccess],
  );

  const handleItemReorder = useCallback(
    (folderId: number, event: SortableReorderEvent<UserBookmark>) => {
      if (skipFolderReorderRef.current.has(folderId)) {
        skipFolderReorderRef.current.delete(folderId);
        clearDragUi();
        return;
      }

      if (event.isExternalDrag) {
        applyCrossFolderMove(
          event.fromItem.id,
          folderId,
          event.toIndex,
          event.fromItem,
        );
        return;
      }

      applyWithinFolderReorder(folderId, event.data);
      clearDragUi();
    },
    [applyCrossFolderMove, applyWithinFolderReorder, clearDragUi],
  );

  const runMoveToFolder = useCallback(
    (bookmarkId: number, toFolderId: number) => {
      applyCrossFolderMove(bookmarkId, toFolderId);
      closeMoveModal();
    },
    [applyCrossFolderMove, closeMoveModal],
  );

  const folderHandlers: FolderSectionHandlers = useMemo(
    () => ({
      toggleExpanded,
      openRename,
      openDelete,
      openMove,
      runRemoveBookmark,
      markFolderHover,
      markCrossDropTarget,
      scheduleFolderHoverExit,
      applyCrossFolderMove,
      handleItemReorder,
      clearDragUi,
    }),
    [
      toggleExpanded,
      openRename,
      openDelete,
      openMove,
      runRemoveBookmark,
      markFolderHover,
      markCrossDropTarget,
      scheduleFolderHoverExit,
      applyCrossFolderMove,
      handleItemReorder,
      clearDragUi,
    ],
  );

  const deleteBookmarkCount = deleteFolder
    ? (folderBookmarks[deleteFolder.id]?.length ?? 0)
    : 0;

  const handleRenameSave = useCallback(
    (name: string) => {
      void runRename(name);
    },
    [runRename],
  );

  const handleCreateSave = useCallback(
    (name: string) => {
      void runCreateFolder(name);
    },
    [runCreateFolder],
  );

  const handleMoveDialogSelect = useCallback(
    (folderId: number) => {
      if (!moveTarget) return;
      runMoveToFolder(moveTarget.bookmark.id, folderId);
    },
    [moveTarget, runMoveToFolder],
  );

  if (loading) {
    return (
      <YStack f={1} bg="$bg" ai="center" jc="center" gap="$3">
        <Spinner color={BRAND_COLORS.muted} />
        <BodyText color={BRAND_COLORS.muted}>Loading bookmarks…</BodyText>
      </YStack>
    );
  }

  if (loadError) {
    return (
      <YStack f={1} bg="$bg" ai="center" jc="center" p="$5" gap="$3">
        <BodyText color={BRAND_COLORS.muted} textAlign="center">
          {loadError.message || "Failed to load bookmarks"}
        </BodyText>
        <Button
          bg={BRAND_COLORS.headerBg}
          color="white"
          br={999}
          onPress={() => void load()}
        >
          Try again
        </Button>
      </YStack>
    );
  }

  return (
    <YStack f={1} bg="$bg">
      <DraxProvider>
        <DraxScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={Platform.OS === "web"}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          keyboardShouldPersistTaps="handled"
        >
          <Container py={isMobile ? "$4" : "$5"}>
            <YStack gap="$4">
              <XStack
                ai="flex-start"
                jc="space-between"
                gap="$3"
                flexWrap="wrap"
              >
                <YStack gap="$2" flex={1} minWidth={200}>
                  <PageTitle tag="h2" fontWeight="700" color="$color">
                    My Bookmarks
                  </PageTitle>
                  <CaptionText color={BRAND_COLORS.muted}>
                    {totalCount === 0
                      ? "Save lessons and courses to find them here."
                      : "Drag to reorder or move between folders."}
                  </CaptionText>
                </YStack>

                <Pressable
                  onPress={() => setCreateFolderOpen(true)}
                  disabled={busy}
                  accessibilityRole="button"
                  accessibilityLabel="Create new folder"
                  style={[styles.newFolderPill, busy && styles.btnDisabled]}
                >
                  <Ionicons
                    name="add"
                    size={18}
                    color={BRAND_COLORS.text}
                  />
                  <CaptionText fontWeight="600" color={BRAND_COLORS.text}>
                    New folder
                  </CaptionText>
                </Pressable>
              </XStack>

              {folders.length === 0 ? (
                <YStack
                  py="$7"
                  px="$5"
                  ai="center"
                  gap="$3"
                  bg={BRAND_COLORS.cardBg}
                  br={16}
                  borderWidth={1}
                  borderColor="rgba(0,0,0,0.06)"
                >
                  <YStack
                    w={48}
                    h={48}
                    br={14}
                    ai="center"
                    jc="center"
                    bg={BRAND_COLORS.pageBg}
                  >
                    <Ionicons
                      name="folder-open-outline"
                      size={22}
                      color={BRAND_COLORS.muted}
                    />
                  </YStack>
                  <YStack gap="$1" ai="center" maxWidth={280}>
                    <CardTitle
                      fontWeight="600"
                      color={BRAND_COLORS.text}
                      textAlign="center"
                    >
                      No folders yet
                    </CardTitle>
                    <CaptionText color={BRAND_COLORS.muted} textAlign="center">
                      Create a folder to start organizing bookmarks.
                    </CaptionText>
                  </YStack>
                </YStack>
              ) : (
                <YStack gap="$3">
                  {folders.map((folder) => {
                    const folderId = Number(folder.id);
                    const items =
                      folderBookmarks[folderId] ??
                      folderBookmarks[folder.id] ??
                      [];
                    const isOpen = expanded[folderId] !== false;
                    const isDropTarget = hoverFolderId === folderId;
                    const crossInsertBeforeId =
                      crossDropTarget?.folderId === folderId
                        ? crossDropTarget.beforeItemId
                        : undefined;

                    return (
                      <BookmarksFolderSection
                        key={folderId}
                        folder={folder}
                        folderId={folderId}
                        items={items}
                        isOpen={isOpen}
                        isDropTarget={isDropTarget}
                        crossInsertBeforeId={crossInsertBeforeId}
                        listEpoch={listEpoch[folderId] ?? 0}
                        busy={busy}
                        handlers={folderHandlers}
                        folderBookmarksRef={folderBookmarksRef}
                        skipFolderReorderRef={skipFolderReorderRef}
                        applyWithinFolderReorderRef={
                          applyWithinFolderReorderRef
                        }
                      />
                    );
                  })}
                </YStack>
              )}
            </YStack>
          </Container>
        </DraxScrollView>
      </DraxProvider>

      <CreateFolderDialog
        open={createFolderOpen}
        busy={busy}
        onClose={closeCreateModal}
        onCreate={handleCreateSave}
      />

      <RenameFolderDialog
        folder={renameFolder}
        busy={busy}
        onClose={closeRenameModal}
        onSave={handleRenameSave}
      />

      <DeleteFolderDialog
        folder={deleteFolder}
        bookmarkCount={deleteBookmarkCount}
        busy={busy}
        onClose={closeDeleteModal}
        onConfirm={runDeleteFolder}
      />

      <MoveBookmarkDialog
        open={!!moveTarget}
        title={moveTarget?.title ?? null}
        targets={moveTargets}
        busy={busy}
        onClose={closeMoveModal}
        onMove={handleMoveDialogSelect}
      />
    </YStack>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: Platform.OS === "web" ? 56 : 32,
  },
  newFolderPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: BRAND_COLORS.border,
    borderRadius: 999,
    backgroundColor: BRAND_COLORS.cardBg,
    paddingVertical: 8,
    paddingHorizontal: 14,
    marginTop: 2,
  },
  btnDisabled: {
    opacity: 0.5,
  },
});
